// ─── HOME LANDING ───────────────────────────────────────────────────
// The signed-out landing page for truetopproducer.com. Moved out of
// App.js so it can be imported by scripts/prerender.mjs without
// pulling in App.js's full import graph. Renamed from LoginPage —
// it is the homepage, not a login screen.

import React, { useState, useEffect, useRef } from 'react'
import { supabase } from './supabase'
import SchoolDisclosure from './SchoolDisclosure'
import { S, FONT_HEAD, FONT_BODY } from './styles'

// One comp card inside the landing page's product shot. Separate from CompCard
// in StrategyCaseView: that one renders real ARMLS rows, this one renders fixed
// marketing copy and owns its own failed-image state so a missing file in
// public/ degrades to a placeholder rather than a broken image.
function ShotCompCard({ src, addr, specs, price, dom }) {
  const [failed, setFailed] = useState(false)
  const chip = { position:'absolute', top:'6px', fontSize:'8px', padding:'2px 6px', borderRadius:'9px',
    fontFamily:FONT_BODY, fontWeight:600, whiteSpace:'nowrap' }
  return (
    <div style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'9px',overflow:'hidden'}}>
      <div style={{position:'relative',height:'92px'}}>
        {failed ? (
          <div style={{width:'100%',height:'100%',background:'#eceae5',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#c4c4c0" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 11.5 12 4l9 7.5"/><path d="M5 10v10h14V10"/><path d="M9 20v-6h6v6"/>
            </svg>
          </div>
        ) : (
          <img src={src} alt="" onError={()=>setFailed(true)}
            style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
        )}
        <div style={{...chip,left:'6px',background:'#FCEBEB',color:'#791F1F'}}>Closed</div>
        <div style={{...chip,right:'6px',background:'#2C2C2A',color:'#F1EFE8'}}>{dom}</div>
      </div>
      <div style={{padding:'7px 8px 8px'}}>
        <div style={{fontSize:'10px',color:'#1d2e42',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{addr}</div>
        <div style={{fontSize:'8.5px',color:'#999',marginTop:'2px'}}>{specs}</div>
        <div style={{fontSize:'13px',fontWeight:600,color:'#147efb',marginTop:'4px'}}>{price}</div>
      </div>
    </div>
  )
}

export function HomeLanding() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showForgot, setShowForgot] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [isMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768)

  // Sign in only. Accounts are created by an admin invite (Admin → Add Agent),
  // never from this page: there is no public sign-up form.
  async function handleSubmit() {
    setError('')
    setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setError(error.message)
    setLoading(false)
  }

  async function handleForgotPassword() {
    if (!forgotEmail.trim()) { setError('Please enter your email'); return }
    setLoading(true); setError('')
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), { redirectTo: window.location.origin })
    if (error) setError(error.message)
    else setError('OK Check your email for a password reset link.')
    setLoading(false)
  }

  // fadeUp, pulse, scanline, glow and shimmer all belonged to the old two-panel
  // split and its blue submit button. What survives is the input focus ring —
  // the only focus affordance these fields have — plus the bar keyframe the
  // product shot's subject strip uses.
  const loginCSS = `
    @keyframes loginBar{0%,100%{transform:scaleY(0.35)}50%{transform:scaleY(1)}}
    .login-input:focus{border-color:#147efb!important;outline:none}
    .login-reveal{opacity:0;transition:opacity .7s cubic-bezier(.16,1,.3,1),transform .7s cubic-bezier(.16,1,.3,1)}
    .login-reveal.in{opacity:1;transform:none!important}
    @media (prefers-reduced-motion: reduce){
      .login-reveal{opacity:1!important;transform:none!important;transition:none!important}
    }
  `

  function goTo(id) {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const wrap = { maxWidth:'880px', margin:'0 auto', padding:isMobile?'0 18px':'0 24px' }
  const sectionLabel = (n, t) => (
    <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px'}}>
      <span style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',fontFamily:FONT_HEAD}}>{n}</span>
      <span style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',fontFamily:FONT_HEAD}}>· {t}</span>
    </div>
  )
  const sectionHead = t => (
    <div style={{fontSize:'20px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,letterSpacing:'-0.3px',marginBottom:'16px'}}>{t}</div>
  )
  const goldPill = { display:'inline-block',background:'#fdbf00',color:'#412402',border:'none',borderRadius:'999px',
    padding:'10px 22px',fontSize:'13px',fontWeight:600,fontFamily:FONT_BODY,cursor:'pointer',textDecoration:'none' }

  const SPECIALISTS = [
    { name:'The Strategist', sub:'Pricing and comps', hb:'#E4EDF7', ht:'#123763', hs:'#185FA5',
      desc:'Live MLS data, five comp buckets, broker-grade analysis.',
      qs:['What should we list at?','Is this appraisal-risky?'] },
    { name:'Inspection desk', sub:'BINSR drafting', hb:'#FAEEDA', ht:'#633806', hs:'#854F0B',
      desc:'Turns inspection findings into language that holds up.',
      qs:['Draft a repair request','Credit instead of repairs?'] },
    { name:'Deal desk', sub:'Contracts and forms', hb:'#EFE6F7', ht:'#46186B', hs:'#534AB7',
      desc:'Arizona REALTORS® and MLS Choice forms, answered against the contract.',
      qs:['Which addendum applies?','What does line 9 mean?'] },
    { name:'Pipeline', sub:'Your database, working', hb:'#DCEEEC', ht:'#0B4A44', hs:'#0F6E56',
      desc:'Your Follow Up Boss database, answering questions instead of sitting there.',
      qs:["Who haven't I called in 90 days?",'Draft a note on this lead'] },
    { name:'Ask Ada', sub:'Arizona license law', hb:'#EAF3DE', ht:'#27500A', hs:'#3B6D11',
      desc:'Arizona real estate law, ADRE rules, and fair housing.',
      qs:['Can I pay a referral fee?','Must my broker be named?'] },
    { name:'Casita Check', sub:'Casitas and ADUs', hb:'#FBE8DA', ht:'#6B3410', hs:'#993C1D',
      desc:'County parcel records, each city\'s casita rules, and Luke AFB zones, with a bilingual client report.',
      qs:['Can this lot fit a casita?','How big can it be?'] },
  ]

  // Fires once when the pipeline section scrolls in, then disconnects. The
  // stagger is per-element transitionDelay rather than four setTimeouts adding
  // a class — same result, and nothing can fire after unmount. Reduced motion
  // is handled entirely in CSS above, so this observer becomes a no-op there
  // rather than needing a matchMedia branch.
  const pipelineRef = useRef(null)
  const [pipelineIn, setPipelineIn] = useState(false)
  useEffect(() => {
    const el = pipelineRef.current
    if (!el || typeof IntersectionObserver === 'undefined') { setPipelineIn(true); return }
    const io = new IntersectionObserver(entries => {
      const hit = entries.some(e => {
        if (!e.isIntersecting) return false
        // Normal case: half the section is inside the margin-adjusted viewport.
        if (e.intersectionRatio >= 0.5) return true
        // A section taller than the viewport can never reach 0.5 — stacked on a
        // short phone this one is ~870px against ~600px of adjusted root, so the
        // ratio tops out below the threshold and the cards would stay invisible
        // forever. Fall back to: its top has crossed the middle of the root.
        const root = e.rootBounds
        return !!root && e.boundingClientRect.top <= root.top + root.height * 0.5
      })
      if (hit) { setPipelineIn(true); io.disconnect() }
    }, { threshold: [0, 0.25, 0.5], rootMargin: '0px 0px -15% 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const revealCls = pipelineIn ? 'login-reveal in' : 'login-reveal'
  // Cards slide in from their own side on desktop; on mobile everything rises.
  const cardFrom = side => pipelineIn ? undefined
    : (isMobile ? 'translateY(40px) scale(.94)' : `translateX(${side === 'left' ? '70px' : '-70px'}) scale(.94)`)

  const PIPELINE_LEFT = [
    { title:'Your link page', line:'One branded link for every bio.',
      path:'<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7L12 19"/>' },
    { title:'Open house pages', line:'A QR code on the door hanger.',
      path:'<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3v3h-3z"/><path d="M19 19h2"/><path d="M19 14h2v2"/>' },
    { title:'Zillow Pro', line:'Part of your tech stack, not a separate seat.',
      path:'<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>' },
  ]
  const PIPELINE_RIGHT = [
    { title:'Your own funnel', line:'A landing page at your name.',
      path:'<path d="M3 4h18l-7 8v7l-4 2v-9L3 4z"/>' },
    { title:'Follow Up Boss', line:'Every lead, tagged and assigned to you.', navy:true,
      path:'<path d="M4 4h16v12H5.5L4 18z"/><path d="M8 9h8"/><path d="M8 12h5"/>' },
    { title:'My wins', line:'Closings designed the moment they happen.',
      path:'<path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10v5a5 5 0 0 1-10 0z"/><path d="M17 5h3v2a3 3 0 0 1-3 3"/><path d="M7 5H4v2a3 3 0 0 0 3 3"/>' },
  ]
  const DELAYS = ['170ms','240ms','310ms']

  const pipeCard = (c, i, side) => (
    <div key={c.title} className={revealCls}
      style={{transform:cardFrom(side),transitionDelay:DELAYS[i],
        background:c.navy?'#1d2e42':'white',border:c.navy?'0.5px solid #1d2e42':'0.5px solid #e8e8e8',
        borderRadius:'12px',padding:'11px 12px'}}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c.navy?'#fdbf00':'#147efb'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{marginBottom:'6px'}} dangerouslySetInnerHTML={{__html:c.path}} />
      <div style={{fontSize:'11.5px',fontWeight:500,color:c.navy?'white':'#1d2e42',marginBottom:'3px'}}>{c.title}</div>
      <div style={{fontSize:'10.5px',color:c.navy?'#B5D4F4':'#666',lineHeight:1.45}}>{c.line}</div>
    </div>
  )

  const MARKETING_CARDS = [
    { title:'Content library', line:'Market reports, articles, and reel scripts — in English and Spanish. Caption written, image ready.',
      path:'<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9h10M7 13h10M7 17h6"/>' },
    { title:'My wins', line:'The moment you list, go pending, or close, your post is already designed and waiting.',
      path:'<path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10v5a5 5 0 0 1-10 0z"/><path d="M17 5h3v2a3 3 0 0 1-3 3"/><path d="M7 5H4v2a3 3 0 0 0 3 3"/>' },
    { title:'Brand templates', line:'Signs, business cards, email signatures, flyers — on brand, ready to edit.',
      path:'<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 9v12"/>' },
    { title:'Weekly emails', line:'A new client email every week, in English and Spanish. Copy it, send it from your own email.',
      path:'<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>' },
  ]

  const JOIN_TILES = [
    ['No per-seat fee','Every tool is included the day you join.'],
    ['Realtor or not','Same tools either way, on the NAR side or the MLS Choice side.'],
    ['Built for Arizona','Live MLS data, AAR and MLS Choice forms, ADRE rules — not a national template.'],
    ['Ask for a change','We built it, so we can change it. Try that with a vendor.'],
  ]

  return (
    <div style={{minHeight:'100vh',background:'#f5f5f5',fontFamily:FONT_BODY}}>
      <style>{loginCSS}</style>

      {/* ── NAV ────────────────────────────────────────────────────────── */}
      <div style={{background:'#f5f5f5',borderBottom:'0.5px solid #e8e8e8',position:'sticky',top:0,zIndex:50}}>
        <div style={{...wrap,display:'flex',alignItems:'center',justifyContent:'space-between',height:'54px',gap:'12px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'9px',minWidth:0}}>
            <span style={{color:'#fdbf00',fontSize:'16px',lineHeight:1}}>&#9654;</span>
            <span style={{fontSize:'14px',fontWeight:900,letterSpacing:'1.5px',color:'#1d2e42',fontFamily:FONT_HEAD,whiteSpace:'nowrap'}}>TrueTopProducer</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:isMobile?'10px':'18px',minWidth:0,overflowX:'auto'}}>
            {!isMobile && (
              <>
                <button onClick={()=>goTo('team')} style={{background:'none',border:'none',padding:0,fontSize:'12.5px',color:'#666',fontFamily:FONT_BODY,cursor:'pointer',whiteSpace:'nowrap'}}>Your team</button>
                <button onClick={()=>goTo('pipeline')} style={{background:'none',border:'none',padding:0,fontSize:'12.5px',color:'#666',fontFamily:FONT_BODY,cursor:'pointer',whiteSpace:'nowrap'}}>Your pipeline</button>
              </>
            )}
            <button onClick={()=>goTo('signin')} style={{background:'none',border:'none',padding:0,fontSize:'12.5px',color:'#666',fontFamily:FONT_BODY,cursor:'pointer',whiteSpace:'nowrap'}}>Sign in</button>
            {/* Request access goes to the recruiting page. There is no public
                sign-up: accounts are created by an admin invite. */}
            <a href="/join"
              style={{background:'#147efb',color:'white',border:'none',borderRadius:'999px',padding:'7px 15px',fontSize:'12.5px',fontWeight:600,fontFamily:FONT_BODY,cursor:'pointer',whiteSpace:'nowrap',flexShrink:0,textDecoration:'none'}}>
              Request access
            </a>
          </div>
        </div>
      </div>

      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <div style={{...wrap,paddingTop:'46px',textAlign:'center'}}>
        <div style={{fontSize:'clamp(34px, 6vw, 58px)',fontWeight:500,color:'#1d2e42',letterSpacing:'-2px',lineHeight:1.02}}>
          The brain of a broker.
        </div>
        <div style={{fontSize:'clamp(34px, 6vw, 58px)',fontWeight:500,color:'#147efb',letterSpacing:'-2px',lineHeight:1.02}}>
          The speed of AI.
        </div>
        <div style={{fontSize:'15px',color:'#666',maxWidth:'420px',margin:'20px auto 0',lineHeight:1.6}}>
          Pricing, inspections, contracts, casitas, your database, license law — answered in seconds, so you walk in ready.
        </div>
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'18px',flexWrap:'wrap',marginTop:'24px'}}>
          <button onClick={()=>goTo('signin')} style={goldPill}>Sign in to the Hub</button>
          {/* Same destination as the nav pill: the recruiting page. */}
          <a href="/join"
            style={{background:'none',border:'none',padding:0,fontSize:'13px',fontWeight:600,color:'#1d2e42',fontFamily:FONT_BODY,cursor:'pointer',textDecoration:'none'}}>
            Request access →
          </a>
        </div>
      </div>

      {/* ── PRODUCT SHOT — replaces the old Strategist band. Every address,
             figure and DOM below is hardcoded marketing copy on a public page.
             Nothing here reads ARMLS or any other API. ──────────────────── */}
      <div style={{...wrap,paddingTop:'44px'}}>
        <div style={{background:'white',border:'0.5px solid #dedede',borderRadius:'14px 14px 0 0',boxShadow:'0 -1px 40px rgba(29,46,66,0.10)',overflow:'hidden'}}>

          {/* chrome */}
          <div style={{padding:'10px 14px',background:'#fafafa',borderBottom:'0.5px solid #e8e8e8',display:'flex',alignItems:'center',gap:'6px'}}>
            {[0,1,2].map(i => <span key={i} style={{width:'9px',height:'9px',borderRadius:'50%',background:'#dcdcdc',flexShrink:0}} />)}
            <div style={{flex:1,textAlign:'center',fontSize:'10.5px',color:'#aaa',fontFamily:FONT_BODY}}>truetopproducer.com</div>
            <div style={{width:'33px',flexShrink:0}} />
          </div>

          <div style={{display:'flex',minHeight:'290px'}}>

            {/* fake rail — desktop only */}
            {!isMobile && (
              <div style={{width:'152px',flexShrink:0,background:'#f4f4f2',borderRight:'0.5px solid #e8e8e8',padding:'12px 9px'}}>
                <div style={{display:'flex',alignItems:'center',gap:'6px',marginBottom:'10px'}}>
                  <span style={{color:'#fdbf00',fontSize:'11px',lineHeight:1}}>&#9654;</span>
                  <span style={{fontSize:'10.5px',fontWeight:900,letterSpacing:'0.5px',color:'#1d2e42',fontFamily:FONT_HEAD}}>TrueTopProducer</span>
                </div>
                <div style={{background:'#fdbf00',borderRadius:'5px',padding:'5px 7px',fontSize:'9px',color:'#412402',marginBottom:'12px',lineHeight:1.3}}>
                  Lead Lab Tuesday 10 AM
                </div>
                <div style={{fontSize:'8.5px',fontWeight:700,letterSpacing:'1px',color:'#999',fontFamily:FONT_HEAD,marginBottom:'5px'}}>YOUR TEAM</div>
                <div style={{background:'white',borderRadius:'6px',padding:'5px 7px',fontSize:'11px',color:'#1d2e42',marginBottom:'2px'}}>The Strategist</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Inspection desk</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Deal desk</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Pipeline</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Ask Ada</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'4px'}}>Casita Check<span style={{background:'#fdbf00',color:'#412402',borderRadius:'12px',padding:'1px 5px',fontSize:'7.5px',fontWeight:600}}>NEW</span></div>
                <div style={{fontSize:'8.5px',fontWeight:700,letterSpacing:'1px',color:'#999',fontFamily:FONT_HEAD,margin:'12px 0 5px'}}>BROKERAGE</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Content</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Weekly emails</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Events</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>My wins</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Link page</div>
              </div>
            )}

            {/* stage */}
            <div style={{flex:1,minWidth:0,padding:'14px 16px'}}>
              <div style={{display:'flex',alignItems:'baseline',justifyContent:'space-between',gap:'10px',marginBottom:'10px'}}>
                <div style={{fontSize:'14px',fontWeight:500,color:'#1d2e42',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>3601 W Tuckey Ln</div>
                <div style={{fontSize:'11px',color:'#999',whiteSpace:'nowrap',flexShrink:0}}>Single family · Seller · 44 comps</div>
              </div>

              <div style={{background:'#1d2e42',borderRadius:'8px',padding:'9px 11px',marginBottom:'11px'}}>
                <div style={{fontSize:'8.5px',fontWeight:700,letterSpacing:'1px',color:'#fdbf00',fontFamily:FONT_HEAD,marginBottom:'6px'}}>SUBJECT</div>
                <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
                  <div style={{background:'white',borderRadius:'5px',padding:'5px 9px',fontSize:'10px',color:'#1d2e42',minWidth:'86px'}}>7060254</div>
                  <div style={{display:'flex',alignItems:'flex-end',gap:'2px',height:'13px',flexShrink:0}}>
                    {['0s','.16s','.32s'].map((d,i)=>(
                      <span key={i} style={{width:'3px',height:'100%',borderRadius:'2px',background:'#e23744',transformOrigin:'bottom',display:'inline-block',animation:'loginBar 1.1s ease-in-out infinite',animationDelay:d}} />
                    ))}
                  </div>
                  <div style={{background:'#fdbf00',color:'#412402',borderRadius:'5px',padding:'5px 11px',fontSize:'10px',fontWeight:600}}>Pull comps</div>
                </div>
              </div>

              <div style={{fontSize:'9px',fontWeight:700,letterSpacing:'1px',color:'#999',fontFamily:FONT_HEAD,marginBottom:'7px'}}>CLOSED · 2</div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'9px'}}>
                {[
                  { src:'/hero-glass.png', addr:'2920 W Glass Ln',  specs:'1,874 sf · 4bd/3ba · 2006', price:'$590,000', dom:'28 DOM' },
                  { src:'/hero-laura.png', addr:'22606 W Laura St', specs:'3,010 sf · 4bd/3ba · 2025', price:'$710,000', dom:'41 DOM' },
                ].map(c => (
                  <ShotCompCard key={c.addr} {...c} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* the disclaimer moved out of the hero and sits under the frame */}
        <div style={{borderTop:'0.5px solid #e8e8e8',marginTop:'26px',paddingTop:'14px',fontSize:'11px',color:'#999',lineHeight:1.55,textAlign:'center',maxWidth:'430px',margin:'26px auto 0'}}>
          A drafting and research tool. You remain responsible for your own work, and your designated broker supervises it.
        </div>
      </div>

      {/* ── 01 · YOUR TEAM ─────────────────────────────────────────────── */}
      <div id="team" style={{...wrap,paddingTop:'40px',scrollMarginTop:'62px'}}>
        {sectionLabel('01','YOUR TEAM')}
        {sectionHead('Your team, one login')}
        {/* Three across on desktop: six cards fill two even rows. */}
        <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(auto-fit,minmax(150px,1fr))':'repeat(3,1fr)',gap:'12px'}}>
          {SPECIALISTS.map(sp => (
            <div key={sp.name} style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',overflow:'hidden',opacity:sp.soon?0.82:1}}>
              <div style={{background:sp.hb,padding:'11px 13px',position:'relative'}}>
                {sp.soon && (
                  <span style={{position:'absolute',top:'9px',right:'10px',background:'white',color:'#666',borderRadius:'999px',padding:'2px 8px',fontSize:'9px',fontWeight:600}}>Coming soon</span>
                )}
                <div style={{fontSize:'13px',fontWeight:600,color:sp.ht}}>{sp.name}</div>
                <div style={{fontSize:'11px',color:sp.hs,marginTop:'2px'}}>{sp.sub}</div>
              </div>
              <div style={{padding:'11px 13px 13px'}}>
                <div style={{fontSize:'11.5px',color:'#666',lineHeight:1.5,marginBottom:'9px'}}>{sp.desc}</div>
                {sp.qs.map(q => (
                  <div key={q} style={{fontSize:'11px',color:'#1d2e42',lineHeight:1.6}}>— {q}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 02 · YOUR PIPELINE — every figure below is marketing copy on a
             public page. Nothing here reads ARMLS or any other API. ─────── */}
      <div id="pipeline" ref={pipelineRef} style={{...wrap,paddingTop:'40px',scrollMarginTop:'62px'}}>
        {sectionLabel('02','YOUR PIPELINE')}
        {sectionHead('It all lands in one place')}

        <div style={{display:'flex',flexDirection:isMobile?'column':'row',alignItems:'center',justifyContent:'center',gap:'14px',flexWrap:'wrap'}}>

          {/* LEFT COLUMN */}
          <div style={{flex:1,maxWidth:isMobile?'100%':'158px',width:isMobile?'100%':undefined,display:'flex',flexDirection:'column',gap:'11px',order:isMobile?2:0}}>
            {PIPELINE_LEFT.map((c,i) => pipeCard(c,i,'left'))}
          </div>

          {/* PHONE */}
          <div className={revealCls}
            style={{flexShrink:0,width:'172px',order:isMobile?1:0,transitionDelay:'0ms',
              transform: pipelineIn ? undefined : 'translateY(26px) scale(.92)',
              background:'#0d0d0f',borderRadius:'30px',padding:'8px 8px 9px',boxShadow:'0 8px 34px rgba(29,46,66,0.20)'}}>
            <div style={{background:'white',borderRadius:'23px',overflow:'hidden',position:'relative'}}>

              {/* dynamic island */}
              <div style={{position:'absolute',top:'7px',left:'50%',transform:'translateX(-50%)',width:'52px',height:'15px',background:'#0d0d0f',borderRadius:'10px',zIndex:2}} />

              {/* status bar */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 13px 4px'}}>
                <span style={{fontSize:'8.5px',fontWeight:600,color:'#1d2e42'}}>9:41</span>
                <span style={{display:'flex',alignItems:'center',gap:'3px'}}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1d2e42" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M4 20v-3"/><path d="M9 20v-7"/><path d="M14 20v-11"/><path d="M19 20V5"/></svg>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1d2e42" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 8.5a15 15 0 0 1 20 0"/><path d="M5.5 12a10 10 0 0 1 13 0"/><path d="M9 15.5a5 5 0 0 1 6 0"/><path d="M12 19h.01"/></svg>
                  <svg width="12" height="9" viewBox="0 0 26 14" fill="none" stroke="#1d2e42" strokeWidth="1.6" aria-hidden="true"><rect x="1" y="1" width="20" height="12" rx="3"/><rect x="3" y="3" width="14" height="8" rx="1.5" fill="#1d2e42" stroke="none"/><path d="M23.5 5v4" strokeLinecap="round"/></svg>
                </span>
              </div>

              {/* app header */}
              <div style={{display:'flex',alignItems:'center',gap:'7px',padding:'6px 11px 7px',background:'#f4f4f2',borderBottom:'0.5px solid #e8e8e8'}}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#1d2e42" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M4 7h16"/><path d="M4 12h16"/><path d="M4 17h16"/></svg>
                <span style={{width:'13px',height:'13px',borderRadius:'3px',background:'#1d2e42',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <span style={{color:'#fdbf00',fontSize:'6px',lineHeight:1}}>&#9654;</span>
                </span>
                <span style={{fontSize:'8.5px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.3px'}}>TrueTopProducer</span>
              </div>

              {/* body */}
              <div style={{padding:'10px',minHeight:'196px'}}>
                <div style={{fontSize:'13px',fontWeight:500,color:'#1d2e42'}}>Ask Ada</div>
                <div style={{fontSize:'8px',color:'#999',marginTop:'2px',lineHeight:1.4}}>Arizona real estate law, ADRE rules, fair housing.</div>

                <div style={{display:'flex',alignItems:'center',gap:'6px',background:'white',border:'0.5px solid #e8e8e8',borderRadius:'9px',padding:'5px 5px 5px 8px',marginTop:'8px'}}>
                  <span style={{flex:1,fontSize:'8px',color:'#bbb'}}>Ask anything…</span>
                  <span style={{width:'15px',height:'15px',borderRadius:'50%',background:'#1d2e42',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5"/><polyline points="5 12 12 5 19 12"/></svg>
                  </span>
                </div>

                <div style={{border:'0.5px solid #147efb',borderRadius:'10px',padding:'8px 9px',marginTop:'9px'}}>
                  <div style={{fontSize:'7px',fontWeight:700,letterSpacing:'1px',color:'#666',fontFamily:FONT_HEAD,marginBottom:'4px'}}>THE STRATEGIST</div>
                  <div style={{fontSize:'9.5px',fontWeight:500,color:'#1d2e42'}}>3601 W Tuckey Ln</div>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'6px',marginTop:'4px'}}>
                    <span style={{fontSize:'7.5px',color:'#999'}}>Last worked 4d ago</span>
                    <span style={{background:'#fdbf00',color:'#412402',borderRadius:'999px',padding:'2px 7px',fontSize:'7.5px',fontWeight:600,flexShrink:0}}>Continue</span>
                  </div>
                </div>

                <div style={{fontSize:'8px',color:'#147efb',marginTop:'9px'}}>Inspection desk →</div>
              </div>

              {/* home indicator */}
              <div style={{padding:'5px 0 7px',display:'flex',justifyContent:'center'}}>
                <span style={{width:'62px',height:'3.5px',borderRadius:'999px',background:'#1d2e42',opacity:0.25}} />
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div style={{flex:1,maxWidth:isMobile?'100%':'158px',width:isMobile?'100%':undefined,display:'flex',flexDirection:'column',gap:'11px',order:isMobile?3:0}}>
            {PIPELINE_RIGHT.map((c,i) => pipeCard(c,i,'right'))}
          </div>
        </div>
      </div>

      {/* ── 03 · YOUR MARKETING ────────────────────────────────────────── */}
      <div id="marketing" style={{...wrap,paddingTop:'40px',scrollMarginTop:'62px'}}>
        {sectionLabel('03','YOUR MARKETING')}
        {sectionHead("Posted before your coffee's cold")}
        <div style={{fontSize:'13px',color:'#666',lineHeight:1.6,maxWidth:'470px',marginTop:'-8px',marginBottom:'16px'}}>
          You didn't get licensed to be a graphic designer. We write it, design it, and translate it — you copy and post.
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:'10px'}}>
          {MARKETING_CARDS.map(c => (
            <div key={c.title} style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'13px 14px'}}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{marginBottom:'8px'}} dangerouslySetInnerHTML={{__html:c.path}} />
              <div style={{fontSize:'12.5px',fontWeight:600,color:'#1d2e42',marginBottom:'4px'}}>{c.title}</div>
              <div style={{fontSize:'11px',color:'#666',lineHeight:1.5}}>{c.line}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 04 · YOUR LICENSE ──────────────────────────────────────────── */}
      <div style={{...wrap,paddingTop:'40px'}}>
        {sectionLabel('04','YOUR LICENSE')}
        {sectionHead('Your CE is already paid for')}
        <div style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',overflow:'hidden',display:'flex',flexDirection:isMobile?'column':'row'}}>
          <div style={{flex:1,padding:'18px 20px'}}>
            <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#0F6E56',fontFamily:FONT_HEAD,marginBottom:'7px'}}>AVANCE SCHOOL OF REAL ESTATE</div>
            <div style={{fontSize:'17px',fontWeight:500,color:'#1d2e42',marginBottom:'8px'}}>The first Arizona school teaching ADRE-approved CE with an AI instructor</div>
            <div style={{fontSize:'12.5px',color:'#666',lineHeight:1.6,marginBottom:'14px',maxWidth:'440px'}}>
              Six courses, approved by the Arizona Department of Real Estate and delivered on an AI-powered platform. Free to every HomePros agent, and reachable from inside the Hub.
            </div>
            <a href="https://avanceschool.com" target="_blank" rel="noopener noreferrer"
              style={{display:'inline-block',background:'#DDEFED',color:'#0B3F3A',borderRadius:'8px',padding:'8px 16px',fontSize:'12px',fontWeight:600,textDecoration:'none'}}>
              Start your CE
            </a>
            {/* 14px matches the column's largest existing gap (the 14px above
                the button), which is the right weight for a change of register
                from marketing copy to school identification. The button is
                inline-block, so its line box adds a few px of descender below
                it — landing the visual gap near the column's own 18px bottom
                padding, so the block sits evenly between the button and the
                card's edge. */}
            <div style={{marginTop:'14px'}}>
              <SchoolDisclosure align="left" />
            </div>
          </div>
          <div style={{width:isMobile?'100%':'190px',flexShrink:0,background:'#DDEFED',padding:'18px 20px',display:'flex',flexDirection:'column',justifyContent:'center'}}>
            <div style={{fontSize:'26px',fontWeight:600,color:'#0B3F3A',lineHeight:1}}>6</div>
            <div style={{fontSize:'11px',color:'#0B3F3A',opacity:0.75,marginTop:'3px'}}>ADRE-approved courses</div>
            <div style={{height:'1px',background:'rgba(11,63,58,0.18)',margin:'12px 0'}} />
            <div style={{fontSize:'26px',fontWeight:600,color:'#0B3F3A',lineHeight:1}}>$0</div>
            <div style={{fontSize:'11px',color:'#0B3F3A',opacity:0.75,marginTop:'3px'}}>For HomePros agents</div>
          </div>
        </div>
      </div>

      {/* ── 05 · JOIN US ───────────────────────────────────────────────── */}
      <div style={{...wrap,paddingTop:'40px'}}>
        {sectionLabel('05','JOIN US')}
        {sectionHead('Built here, not bought')}
        <div style={{background:'#1d2e42',borderRadius:'12px',padding:'26px'}}>
          <div style={{fontSize:isMobile?'22px':'26px',fontWeight:500,color:'white',lineHeight:1.3}}>Most brokerages rent their tools.</div>
          <div style={{marginTop:'6px'}}>
            <span style={{display:'inline-block',background:'#fdbf00',color:'#412402',borderRadius:'6px',padding:'2px 12px',fontSize:isMobile?'22px':'26px',fontWeight:500,lineHeight:1.35}}>We build ours.</span>
          </div>
          <div style={{fontSize:'13.5px',color:'#B5D4F4',lineHeight:1.6,maxWidth:'460px',marginTop:'14px'}}>
            The Strategist, Casita Check, the Inspection desk, the Deal desk, Pipeline, Ada, your link page, your open house pages, your closing cards, your weekly emails — none of it is someone else's software we resell you. It was built here, for our agents, by the broker who signs your license.
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:'10px',marginTop:'20px'}}>
            {JOIN_TILES.map(([t,l]) => (
              <div key={t} style={{background:'rgba(255,255,255,0.06)',borderRadius:'10px',padding:'12px 13px'}}>
                <div style={{fontSize:'12.5px',fontWeight:600,color:'white',marginBottom:'4px'}}>{t}</div>
                <div style={{fontSize:'11.5px',color:'#B5D4F4',lineHeight:1.5}}>{l}</div>
              </div>
            ))}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:'14px',flexWrap:'wrap',marginTop:'20px'}}>
            <a href="/join" style={goldPill}>See what joining looks like</a>
            <a href="tel:6026930218" style={{fontSize:'12.5px',color:'#B5D4F4',textDecoration:'none'}}>or call 602-693-0218</a>
          </div>
        </div>
      </div>

      {/* ── SIGN IN — the real form: sign in and password reset only. ── */}
      <div id="signin" style={{...wrap,paddingTop:'40px',paddingBottom:'56px',scrollMarginTop:'62px'}}>
        <div style={{background:'#1d2e42',borderRadius:'12px',padding:isMobile?'24px 20px':'30px',textAlign:'center'}}>
          <div style={{fontSize:'22px',fontWeight:500,color:'white'}}>
            Already with HomePros?
          </div>
          <div style={{fontSize:'13px',color:'#B5D4F4',lineHeight:1.6,maxWidth:'380px',margin:'8px auto 0'}}>
            Sign in with your brokerage email. Everything above is already waiting for you.
          </div>

          <div style={{maxWidth:'300px',margin:'22px auto 0',textAlign:'left'}}>
            <div style={S.field}>
              <label style={{...S.label,color:'#B5D4F4'}}>EMAIL</label>
              <input className="login-input" style={{...S.input,border:'1px solid #ddd',background:'white'}} type="email" placeholder="you@homepros.com" value={email} onChange={e=>setEmail(e.target.value)} />
            </div>
            <div style={S.field}>
              <label style={{...S.label,color:'#B5D4F4'}}>PASSWORD</label>
              <input className="login-input" style={{...S.input,border:'1px solid #ddd',background:'white'}} type="password" placeholder="••••••••" value={password}
                onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSubmit()} />
            </div>
            {!showForgot && (
              <div style={{marginBottom:'16px',textAlign:'right'}}>
                <button onClick={()=>setShowForgot(true)} style={{background:'none',border:'none',color:'#B5D4F4',fontSize:'12px',fontFamily:FONT_BODY,cursor:'pointer',padding:0}}>Forgot Password?</button>
              </div>
            )}
            {showForgot && (
              <div style={{marginBottom:'16px',background:'rgba(255,255,255,0.06)',borderRadius:'8px',padding:'14px'}}>
                <label style={{...S.label,color:'#B5D4F4'}}>RESET PASSWORD EMAIL</label>
                <input className="login-input" style={{...S.input,border:'1px solid #ddd',background:'white',marginBottom:'10px'}} type="email" placeholder="you@homepros.com" value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)} />
                <div style={{display:'flex',gap:'8px'}}>
                  <button style={{...S.btnPrimary,flex:1,padding:'8px',fontSize:'12px'}} onClick={handleForgotPassword} disabled={loading}>Send Reset Link</button>
                  <button style={{...S.btnOutline,flex:1,padding:'8px',fontSize:'12px',color:'#B5D4F4',borderColor:'rgba(255,255,255,0.2)'}} onClick={()=>setShowForgot(false)}>Cancel</button>
                </div>
              </div>
            )}
            {error && (
              <div style={{...S.errorBox,...(error.startsWith('OK')?{color:'#2d7a2d',borderColor:'rgba(45,122,45,0.3)',background:'rgba(45,122,45,0.06)'}:{background:'rgba(200,16,46,0.12)'})}}>
                {error.startsWith('OK') ? error.replace('OK ','') : error}
              </div>
            )}
            <button style={{
              width:'100%',padding:'13px',background:'#fdbf00',color:'#412402',border:'none',borderRadius:'999px',
              fontSize:'13px',fontWeight:600,fontFamily:FONT_BODY,
              cursor:loading?'not-allowed':'pointer',opacity:loading?0.6:1,
            }} onClick={handleSubmit} disabled={loading}>
              {loading?'Please wait...':'Sign in'}
            </button>

            <div style={{fontSize:'12px',color:'#B5D4F4',textAlign:'center',marginTop:'16px'}}>
              Not with us yet? <a href="/demo" style={{color:'#fdbf00',fontWeight:600,textDecoration:'none'}}>Book a 20-minute demo</a> or <a href="/join" style={{color:'#fdbf00',fontWeight:600,textDecoration:'none'}}>see what joining looks like</a>
            </div>
            <div style={{fontSize:'11px',color:'#B5D4F4',textAlign:'center',marginTop:'14px',opacity:0.8}}>New accounts are created by invitation from your broker.</div>
          </div>
        </div>

        <div style={{textAlign:'center',marginTop:'22px',fontSize:'10px',letterSpacing:'2px',color:'#999',fontFamily:FONT_HEAD}}>
          HOMEPROS HUB · PHOENIX, AZ
        </div>
        <div style={{display:'flex',justifyContent:'center',gap:'18px',marginTop:'10px',fontSize:'12px',fontFamily:FONT_BODY}}>
          <a href="/privacy" style={{color:'#666',textDecoration:'none'}}>Privacy Policy</a>
          <a href="/terms" style={{color:'#666',textDecoration:'none'}}>Terms of Use</a>
        </div>
      </div>
    </div>
  )
}
